import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const alertVariants = cva(
  "relative grid w-full grid-cols-[0_1fr] items-start gap-y-0.5 rounded-lg border px-4 py-3 text-sm has-[>svg]:grid-cols-[calc(var(--spacing)*4)_1fr] has-[>svg]:gap-x-3 [&>svg]:size-4 [&>svg]:translate-y-0.5 [&>svg]:text-current",
  {
    variants: {
      variant: {
        default: "bg-card text-card-foreground",
        destructive:
          "bg-card text-destructive *:data-[slot=alert-description]:text-destructive/90 [&>svg]:text-current",
        // `warning` and `info` close a real gap, not a stylistic preference.
        // `components/shared/launch-drops-banner.tsx` says so in a comment:
        // "The shadcn <Alert> primitive only ships default / destructive
        // variants, so we hand-roll the amber/warning look to match house
        // style" — and `external-recipients-warning-card.tsx` hand-rolls the
        // same thing again. Every AI degradation surface (~40 of them) needed
        // an amber "running low" and a blue "reduced mode", which is 40 more
        // hand-rolled divs and 40 chances to forget a `dark:` counterpart.
        //
        // Tint values are lifted verbatim from `components/ui/status-badge.tsx`
        // so a warning alert and a warning badge on the same screen are the
        // same amber, and both survive dark mode without further work.
        warning:
          "bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-500/25 *:data-[slot=alert-description]:text-amber-700/90 dark:*:data-[slot=alert-description]:text-amber-400/90 [&>svg]:text-current",
        info: "bg-blue-500/10 text-blue-700 dark:text-blue-400 border-blue-500/25 *:data-[slot=alert-description]:text-blue-700/90 dark:*:data-[slot=alert-description]:text-blue-400/90 [&>svg]:text-current",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
)

function Alert({
  className,
  variant,
  ...props
}: React.ComponentProps<"div"> & VariantProps<typeof alertVariants>) {
  return (
    <div
      data-slot="alert"
      role="alert"
      className={cn(alertVariants({ variant }), className)}
      {...props}
    />
  )
}

function AlertTitle({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="alert-title"
      className={cn(
        "col-start-2 line-clamp-1 min-h-4 font-medium tracking-tight",
        className
      )}
      {...props}
    />
  )
}

function AlertDescription({
  className,
  ...props
}: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="alert-description"
      className={cn(
        "col-start-2 grid justify-items-start gap-1 text-sm text-muted-foreground [&_p]:leading-relaxed",
        className
      )}
      {...props}
    />
  )
}

export { Alert, AlertTitle, AlertDescription }
